/**
 * Single source of truth for everything personal on the site.
 * Edit here and every section updates.
 */

export const profile = {
  firstName: 'Qasim',
  name: 'Qasim Afzaal',
  role: 'Software Engineer & Product Builder',
  tagline: 'Mobile · Web · Backend · SaaS · Cloud',
  location: 'Lahore, Pakistan',
  timezone: 'PKT (UTC+5)',
  email: 'qasim.afzaal432@gmail.com',
  photo: '/me.JPEG',
  domain: 'https://qasimafzaal.com',

  // Served from /public (source CV: Qasim Afzaal Senior Mobile App Developer)
  resume: '/Qasim-Afzaal-Resume.pdf',

  availability: 'Open to new projects',
  responseTime: 'Usually replies within a few hours',

  headline: {
    lead: 'Building digital products',
    accent: 'that scale.'
  },

  intro:
    "Software engineer with 6+ years turning ideas into mobile, web, and SaaS products people actually enjoy using. I like hard problems, clean architecture, and shipping work that holds up.",

  social: {
    github: 'https://github.com/Qasim-afzaal',
    linkedin: 'https://www.linkedin.com/in/qasim-dev/',
    twitter: 'https://x.com/mqasimtech?s=21',
    instagram: 'https://www.instagram.com/official_qasim_ali?igsh=MWN1eGw1aWtkM2Z3Yw%3D%3D&utm_source=qr'
  }
};

export const mailto = (subject = "Let's work together") =>
  `mailto:${profile.email}?subject=${encodeURIComponent(subject)}`;

export const stats = [
  { label: 'Years experience', value: 6, suffix: '+' },
  { label: 'Projects delivered', value: 20, suffix: '+' },
  { label: 'Teams worked with', value: 15, suffix: '+' },
  { label: 'Countries worked with', value: 8, suffix: '+' }
];

export const about = {
  journey: [
    "I started with small Android apps just to see my own ideas running on a phone. That curiosity became a career. Over six years I've shipped products for early startups and for teams whose software thousands of people rely on every day.",
    "Most of my work sits where mobile, web, and backend meet. I like owning a feature completely: the data model, the API, the screen, and the quiet details nobody notices until they're missing."
  ],
  philosophy: [
    {
      icon: 'simple',
      title: 'Simple beats clever',
      body: 'The best code is readable at 2am. I optimize for clarity first and only get clever when the problem truly needs it.'
    },
    {
      icon: 'understand',
      title: 'Understand the problem first',
      body: 'Too much engineering time goes into building the wrong thing well. I ask early, prove the idea small, then expand.'
    },
    {
      icon: 'ship',
      title: 'Ship, then refine',
      body: 'Real feedback beats a perfect plan. I get something working in front of people fast, then improve what usage shows.'
    },
    {
      icon: 'details',
      title: 'Details are the product',
      body: 'Loading states, empty states, error copy, animation timing. Users never mention them when done right. They never forget when done wrong.'
    }
  ],
  enjoys: [
    'Products with a real, messy problem underneath',
    'Architecture that holds up as teams grow',
    'Interfaces that feel fast and quiet',
    'Automation that removes boring work'
  ]
};

export const skillGroups = [
  {
    key: 'mobile',
    name: 'Mobile',
    blurb: 'Apps that feel native on both stores.',
    skills: ['Flutter', 'Dart', 'React Native', 'Swift / iOS', 'Kotlin / Android', 'Riverpod & BLoC']
  },
  {
    key: 'frontend',
    name: 'Frontend',
    blurb: 'Interfaces that stay fast as features grow.',
    skills: ['React', 'Next.js', 'TypeScript', 'Tailwind CSS', 'Framer Motion', 'Redux / Zustand']
  },
  {
    key: 'backend',
    name: 'Backend',
    blurb: 'APIs built to be calm and dependable.',
    skills: ['Node.js', 'NestJS', 'Express', 'Python / FastAPI', 'GraphQL', 'REST & WebSockets']
  },
  {
    key: 'databases',
    name: 'Databases',
    blurb: 'Data models that stay sane past 100k rows.',
    skills: ['PostgreSQL', 'MongoDB', 'Firestore', 'Redis', 'Prisma', 'SQLite / Drift']
  },
  {
    key: 'cloud',
    name: 'Cloud',
    blurb: 'Infrastructure sized to the product, not the hype.',
    skills: ['AWS', 'Google Cloud', 'Firebase', 'Vercel', 'Cloud Functions', 'S3 & CloudFront']
  },
  {
    key: 'devops',
    name: 'DevOps',
    blurb: 'Pipelines that make releases uneventful.',
    skills: ['Docker', 'Kubernetes', 'GitHub Actions', 'Fastlane', 'CI/CD', 'Monitoring & Sentry']
  },
  {
    key: 'ai',
    name: 'AI',
    blurb: 'Practical AI wired into real products. Learning deeper every week.',
    skills: ['OpenAI API', 'LangChain', 'RAG pipelines', 'Machine learning', 'Vector search', 'Prompt engineering', 'LLM agents', 'Embeddings']
  },
  {
    key: 'architecture',
    name: 'Architecture',
    blurb: 'Structure that survives its own success.',
    skills: ['Clean Architecture', 'Domain driven design', 'Microservices', 'Event driven systems', 'Design patterns', 'Code review & mentoring']
  }
];

export const experience = [
  {
    company: 'Techinoid',
    role: 'Senior Software Engineer',
    period: '2024 to Present',
    summary:
      'Building modern digital products across mobile, web, and backend. Owning features end to end and helping the team move from idea to production without the usual drama.',
    achievements: [
      'Leading delivery across Flutter, React, and Node',
      'Shipping AI features into real product flows, not demos',
      'Raising code quality through reviews, clearer architecture, and faster releases'
    ],
    tech: ['Flutter', 'React', 'Node.js', 'AWS', 'PostgreSQL', 'OpenAI', 'LangChain']
  },
  {
    company: 'Norgic',
    role: 'Software Engineer',
    period: '2021 to 2024',
    summary:
      'Built production apps across mobile and backend, usually as the engineer bridging design, APIs, and the experience users actually touch.',
    achievements: [
      'Shipped multiple production apps to the App Store and Play Store',
      'Owned features from data model through UI to release',
      'Stabilized legacy codebases so releases stopped being stressful'
    ],
    tech: ['Flutter', 'Firebase', 'Node.js', 'GraphQL', 'PostgreSQL', 'AWS']
  }
];

export const processSteps = [
  {
    number: '01',
    title: 'Discover',
    body: 'We talk through the problem, the users, and the constraints. I ask a lot of questions here so we build the right thing.'
  },
  {
    number: '02',
    title: 'Plan',
    body: 'Scope, architecture, and a realistic timeline. You get a clear picture of what ships, in what order, and why.'
  },
  {
    number: '03',
    title: 'Build',
    body: 'Short iterations with something to look at every week. No black boxes. No month long silences.'
  },
  {
    number: '04',
    title: 'Test',
    body: 'Automated tests, real device checks, and performance passes before anything reaches your users.'
  },
  {
    number: '05',
    title: 'Launch',
    body: 'Store submissions, deployment, monitoring. Then I stay close for the first weeks, when it matters most.'
  }
];

export const beyond = {
  currentFocus: [
    'Going deep on AI and ML: RAG, LangChain, and agents that ship in real products',
    'Building AI features that solve real problems, not demos',
    'Getting sharper at system design as products grow'
  ],
  exploring: [
    'RAG & vector search',
    'LangChain',
    'LLM agents',
    'Machine learning',
    'Prompt engineering',
    'Embeddings',
    'Local first AI',
    'Edge inference'
  ],
  openSource: [
    {
      name: 'Flutter utilities',
      description: 'Small packages and helpers I keep reusing across projects.',
      url: profile.social.github
    },
    {
      name: 'Starter templates',
      description: 'Opinionated scaffolds for Flutter and Node services.',
      url: profile.social.github
    }
  ],
  writing: [
    { title: 'Structuring Flutter apps that outlive their first release', status: 'Coming soon' },
    { title: 'Adding AI to a product without breaking it', status: 'Coming soon' },
    { title: 'What I learned rescuing a legacy codebase', status: 'Coming soon' }
  ],
  certifications: [
    { name: 'Google Associate Android Developer', issuer: 'Google' },
    { name: 'AWS Certified Cloud Practitioner', issuer: 'Amazon Web Services' }
  ],
  speaking: [
    { title: 'Flutter beyond the widget tree', event: 'Local dev meetup', year: '2024' },
    { title: 'Shipping mobile apps solo', event: 'Community workshop', year: '2023' }
  ],
  funFacts: [
    'I redesign my own portfolio far more often than I should',
    'Most of my best debugging happens on a walk, away from the screen',
    'I keep a running notes file of ideas I will probably never build',
    'Coffee powered, but genuinely trying to switch to tea'
  ]
};

export const testimonials = [
  {
    quote:
      'Qasim took a vague idea and came back with questions we had not even thought to ask. The app shipped on time and has needed almost no firefighting since.',
    name: 'Dennis',
    role: 'Founder',
    company: 'Product team',
    rating: 5
  },
  {
    quote:
      'Rare combination: writes genuinely clean code and can explain the trade offs to non technical people without making them feel lost.',
    name: 'James',
    role: 'Product Manager',
    company: 'SaaS product',
    rating: 5
  },
  {
    quote:
      'He inherited a messy codebase and made it boring in the best possible way. Releases stopped being stressful.',
    name: 'Nicole',
    role: 'Engineering lead',
    company: 'Scale up',
    rating: 5
  }
];

export const navItems = [
  { name: 'About', href: '#about' },
  { name: 'Work', href: '#work' },
  { name: 'Services', href: '#services' },
  { name: 'Skills', href: '#skills' },
  { name: 'Experience', href: '#experience' }
];
